import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Copy, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import {
  geniePageUrl,
  isPageLive,
  pageBlockedReason,
} from "@/pages/UserHub/GeniePages/geniePageStatus";
import { Agent } from "@/types/agent/agent";

interface GeniePageCardProps {
  agent: Agent;
  brand?: string;
}

/**
 * This Genie's public page, from inside the Genie.
 *
 * Deliberately a summary and a way through, not a second editor — everything
 * configurable lives at `/genie-hub/genie-pages/:genieId`, and the content
 * itself belongs to the knowledge base and the Resources card above.
 */
export const GeniePageCard = ({ agent, brand = "#4E9CFF" }: GeniePageCardProps) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const url = geniePageUrl(agent);
  const live = isPageLive(agent);
  const blockedReason = pageBlockedReason(agent);
  const hub = agent.hub_config;

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Page link copied");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link — you can select it from the page instead.");
    }
  };

  return (
    <OverviewSectionCard
      title="Genie Page"
      subtitle="The public page anyone can visit to get help from this Genie"
      genieIcon="web-page"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
              live ? "bg-[#EBF5FF] text-[#2563EB]" : "bg-gray-100 text-gray-500"
            }`}
          >
            {live ? "Live" : "Not public"}
          </span>
          {url && (
            <span className="min-w-0 truncate font-mono text-[12.5px] text-gray-500">
              {url.replace(/^https?:\/\//, "")}
            </span>
          )}
        </div>

        {blockedReason ? (
          <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
            {blockedReason}
          </p>
        ) : (
          <p className="text-[13px] leading-snug text-gray-600">
            Carries the Genie itself, {hub?.featuredQuestions?.length
              ? `${hub.featuredQuestions.length} featured questions`
              : "the questions your guides answer"}
            , your published guides, your manuals and every way to reach a person.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => navigate(`/genie-hub/genie-pages/${agent.id}`)}
          >
            <Settings2 className="h-4 w-4" />
            Design page
          </Button>
          {live && url && (
            <>
              <a
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
              >
                Open
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <Button variant="outline" className="gap-1.5" onClick={() => void handleCopy()}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                Copy link
              </Button>
            </>
          )}
        </div>
      </div>
    </OverviewSectionCard>
  );
};

export default GeniePageCard;
